import { Module } from 'vuex';
import { solarLead as SolarLeadApi } from '@/api';
import AjaxErrorResponseInterface from '@/interfaces/AjaxErrorResponseInterface';

const defaultFormData = {
  // address
  postcode: null as string | number | null,
  city: null as string | null,
  street: null as string | null,
  houseNumber: null as string | null,
  // roof
  roof: {
    area: null as string | null,
    material: null as string | null,
    tilt: null as string | null,
    orientation: null as string | null,
    buildInLast30Years: 'Ich weiß es nicht',
  },
  // power
  powerUsage: null as number | null,
  time: 'morgens & abends',
  storage: 'Nein',
  // wallbox
  electricVehicle: null as number | null,
  car: {
    usage: null,
    type: null,
    yearly: null,
  },
  // montage
  startInstallation: null as string | null,
  otherInformations: null as string | null,
  // contact
  salutation: 'Herr',
  firstName: null as string | null,
  lastName: null as string | null,
  contactAddressDifferent: false,
  isCustomer: false,
  contactAddress: {
    postcode: null as string | number | null,
    city: null as string | null,
    street: null as string | null,
    houseNumber: null as string | null,
  },
  phone: null as string | null,
  email: null as string | null,
  reachability: null as string | null,
  // overview
  owner: false,
  privacy: false,
  errors: [],
};

const leadFormModule: Module<any, any> = {
  namespaced: true,

  state: {
    isActive: false,
    isLoading: false,
    possibleSteps: {
      address: {
        title: 'Lage des Hauses',
      },
      roof: {
        title: 'Dacheigenschaften',
      },
      power: {
        title: 'Stromverbrauch',
      },
      wallbox: {
        title: 'Wallbox',
      },
      montage: {
        title: 'Montage',
      },
      contact: {
        title: 'Kontaktdaten',
      },
      overview: {
        title: 'Alles richtig? Bitte prüfen Sie zum Abschluss ihre Daten.',
      },
      success: {},
    },
    activeStep: 'address',
    data: { ...defaultFormData },
    dataOld: { ...defaultFormData },
    errors: {} as Record<string, string | Array<string>>,
    state: 'running',
    isEdit: false,
  },

  mutations: {
    setActiveStep(state, stepKey) {
      state.activeStep = stepKey;
      state.isEdit = false;
    },
    setEditStep(state, stepKey) {
      state.activeStep = stepKey;
      state.dataOld = { ...state.data };
      state.isEdit = true;
    },
    setData(state, data) {
      state.data = data;
    },
    setErrors(state, errors) {
      state.errors = errors;
    },
    setIsLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setIsActive(state, isActive) {
      state.isActive = isActive;
    },
  },

  actions: {
    scrollToTop() {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }, 100);
    },
    entry({ commit, state, dispatch }) {
      commit('setIsActive', true);
      state.dataOld = { ...defaultFormData };
      state.data = { ...defaultFormData };
      dispatch('scrollToTop');
    },
    toNextStep({ commit, state, dispatch }) {
      const stepKeys = Object.keys(state.possibleSteps);
      let newStepInx = stepKeys.indexOf(state.activeStep) + 1;

      if (newStepInx > (stepKeys.length - 1)) {
        newStepInx = stepKeys.length - 1;
      }

      commit('setActiveStep', stepKeys[newStepInx]);
      dispatch('scrollToTop');
    },
    toPreviousStep({ commit, state, dispatch }) {
      const stepKeys = Object.keys(state.possibleSteps);
      const newStepInx = stepKeys.indexOf(state.activeStep) - 1;

      if (newStepInx < 0) {
        commit('setIsActive', false);
      } else {
        commit('setActiveStep', stepKeys[newStepInx]);
      }

      dispatch('scrollToTop');
    },
    cancelEdit({ commit, state }) {
      commit('setData', state.dataOld);
      commit('setErrors', {});
      commit('setActiveStep', 'overview');
    },
    validate({ state, dispatch, commit }) {
      SolarLeadApi
        .validate(state.activeStep, state.data)
        .then(() => {
          if (state.isEdit) {
            commit('setActiveStep', 'overview');
          } else {
            dispatch('toNextStep');
          }
        }).catch((errorResponse: AjaxErrorResponseInterface) => {
          state.errors = errorResponse.data.errors || {};
        })
        .finally(() => {
          commit('setIsLoading', false);
        });
    },
    submit({ state, commit }) {
      SolarLeadApi
        .submit(state.data)
        .then(() => {
          state.state = 'succeeded';
          commit('setIsActive', false);
        }).catch((errorResponse: AjaxErrorResponseInterface) => {
          state.errors = errorResponse.data.errors || {};
        })
        .finally(() => {
          commit('setIsLoading', false);
        });
    },
    submitCurrentStep({ commit, state, dispatch }) {
      commit('setIsLoading', true);
      commit('setErrors', {});

      if (state.activeStep === 'overview') {
        dispatch('submit');
      } else {
        dispatch('validate');
      }
    },
  },

  getters: {
    data: (state) => state.data,
    isEdit: (state) => state.isEdit,
    state: (state) => state.state,
    errors: (state) => state.errors,
    isLoading: (state) => state.isLoading,
    activeStep: (state) => ({
      key: state.activeStep,
      component: `Step${state.activeStep[0].toUpperCase()}${state.activeStep.slice(1)}`,
      ...state.possibleSteps[state.activeStep],
    }),
    stepPercentage: (state) => ((Object.keys(state.possibleSteps).indexOf(state.activeStep) + 1) / Object.keys(state.possibleSteps).length) * 100,
  },
};

export default leadFormModule;
